import * as borogove from "borogove";
import { readyServiceWorker } from "../util";
import { Link, Navigate, useOutletContext } from "react-router";
import { Context } from "../App";
import { useEffect, useRef, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";

const registerPush = (
	client: borogove.Client,
	idbp: Awaited<ReturnType<typeof borogove.persistence.IDB>>,
) => {
	if (!import.meta.env.VITE_PUSH_SERVICE) {
		console.warn("No WebPush service configured, skipping");
		return;
	}

	client.addStatusOnlineListener(() => {
		(async () => {
			const serviceWorker = await readyServiceWorker;
			if (!serviceWorker || !Notification?.permission) return;
			// public key can be computed from private key, but then we need code to do that, so we store both
			let key = (await idbp.get("vapid_key")) as {
				publicKey: ArrayBuffer;
				privateKey: ArrayBuffer;
			};
			let keyPair;
			if (key) {
				const publicKey = await window.crypto.subtle.importKey(
					"raw",
					key.publicKey,
					{
						name: "ECDSA",
						namedCurve: "P-256",
					},
					true,
					["verify"],
				);
				const privateKey = await window.crypto.subtle.importKey(
					"pkcs8",
					key.privateKey,
					{
						name: "ECDSA",
						namedCurve: "P-256",
					},
					true,
					["sign"],
				);
				keyPair = { publicKey, privateKey };
			} else {
				keyPair = await window.crypto.subtle.generateKey(
					{
						name: "ECDSA",
						namedCurve: "P-256",
					},
					true,
					["sign", "verify"],
				);
				key = {
					publicKey: await window.crypto.subtle.exportKey(
						"raw",
						keyPair.publicKey,
					),
					privateKey: await window.crypto.subtle.exportKey(
						"pkcs8",
						keyPair.privateKey,
					),
				};
				await idbp.set("vapid_key", key);
			}
			client.subscribePush(
				serviceWorker,
				import.meta.env.VITE_PUSH_SERVICE,
				keyPair,
				144,
			);
		})();
	});
};

export const newAccount = (
	accountId: string,
	idbp: Awaited<ReturnType<typeof borogove.persistence.IDB>>,
	persistence: borogove.Persistence,
) => {
	const client = new borogove.Client(accountId, persistence);
	registerPush(client, idbp);
	setTimeout(() => client.start(), 0);

	return {
		client,
		status: "connecting",
	};
};

export const Login = () => {
	const { accounts, setAccounts, persistence, idbp } =
		useOutletContext<Context>();
	const [accountId, setAccountId] = useState<string>();
	const [address, setAddress] = useState("");
	const addressInput = useRef<HTMLInputElement>(null);
	const [authAttempts, setAuthAttempts] = useState(0);

	const account = accountId ? accounts[accountId] : null;

	useEffect(() => {
		if (!accountId) addressInput.current?.focus();
	}, [accountId]);

	const setStatus = (status: string) => {
		if (!accountId) return;

		setAccounts({
			...accounts,
			[accountId]: { ...accounts[accountId], status },
		});
	};

	if (account?.status === "online") {
		return (
			<Navigate
				to={`/accounts/${encodeURIComponent(account.client.accountId()).replace(/%40/, "@")}`}
			/>
		);
	}

	const connectError = account?.status === "error";
	const needAuth = account?.status === "auth";
	return (
		<div id="login">
			<form
				onSubmit={(e) => {
					e.preventDefault();
					if (!account || connectError) {
						if (!e.currentTarget.jid.value) return;

						setAuthAttempts(0);
						const newAccountId = e.currentTarget.jid.value;
						setAccountId(newAccountId);
						setAccounts((existing) => {
							const accounts = { ...existing };
							if (accountId && connectError) delete accounts[accountId];
							if (accounts[newAccountId]) return accounts;

							return {
								...accounts,
								[newAccountId]: newAccount(newAccountId, idbp, persistence),
							};
						});
					} else {
						setAuthAttempts(authAttempts + 1);
						account.client.usePassword(e.currentTarget.password.value);
						setStatus("connecting");
					}
				}}
			>
				<h1>Cheogram</h1>
				<p className="slogan">Connect to all your contacts</p>
				{account && !connectError ? (
					<div className="login-address">
						<span title={address}>{address}</span>
						{needAuth && (
							<button
								type="button"
								className="login-change"
								aria-label="Change Jabber ID"
								onClick={() => {
									setAccountId(undefined);
									setAuthAttempts(0);
								}}
							>
								Change
							</button>
						)}
					</div>
				) : (
					<input
						ref={addressInput}
						name="jid"
						type="text"
						placeholder="Jabber ID"
						value={address}
						onChange={(e) => setAddress(e.currentTarget.value)}
					/>
				)}
				{connectError && (
					<Alert severity="error">Server connection failed</Alert>
				)}
				{needAuth && (
					<input
						autoFocus
						name="password"
						type="password"
						placeholder="Enter your password"
					/>
				)}
				{needAuth && authAttempts > 0 && (
					<Alert severity="error">Login failed. Please try again.</Alert>
				)}
				{!account || connectError || needAuth ? (
					<button>{account && !connectError ? "Login" : "Next"}</button>
				) : (
					<CircularProgress color="secondary" />
				)}
				{!account && (
					<Link to="/register?with=jabber.fr">Don&#39;t have an account?</Link>
				)}
			</form>
		</div>
	);
};
